import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  makeStyles,
  IconButton
} from '@material-ui/core';
import {
  ExpandMore as ExpandMoreIcon,
  Home as HomeIcon,
  Description as DescriptionIcon,
  Event as EventIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh
} from '@material-ui/icons';
import { getDashboardAlerts, getDashboardSystemSetupProgress } from '../../services/dashboardService';
import AlertDetail from './AlertDetail';
import { getTranslation } from '../../heplers/translationHelper';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: 320,
    padding: theme.spacing(2)
  },
  setupCard: {
    marginBottom: theme.spacing(2),
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white'
  },
  headerBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  percentageText: {
    fontSize: '2rem',
    fontWeight: 700
  },
  mainProgress: {
    height: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(255,255,255,0.3)',
    marginBottom: theme.spacing(2),
    '& .MuiLinearProgress-bar': {
      backgroundColor: 'red',
      borderRadius: 4
    }
  },
  mainProgressCompleted: {
    height: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(255,255,255,0.3)',
    marginBottom: theme.spacing(2),
    '& .MuiLinearProgress-bar': {
      backgroundColor: 'rgba(5, 211, 74, 0.3)',
      borderRadius: 4
    }
  },
  categoryContainer: {
    marginBottom: theme.spacing(1.5)
  },
  categoryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5)
  },
  categoryProgress: {
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.2)',
    '& .MuiLinearProgress-bar': {
      backgroundColor: 'white',
      borderRadius: 2
    }
  },
  setupButton: {
    marginTop: theme.spacing(2),
    color: 'white',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.1)'
    }
  },
  alertsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5)
  },
  alertList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  alertItem: {
    padding: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    borderRadius: 8,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: 3,
      transition: 'width 0.2s ease'
    }
  },
  alertItemError: {
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
    '&::before': {
      backgroundColor: '#EF4444'
    },
    '&:hover': {
      backgroundColor: '#FEE2E2',
      transform: 'translateX(2px)',
      boxShadow: '0 2px 8px rgba(239, 68, 68, 0.15)',
      '&::before': {
        width: 4
      }
    }
  },
  alertItemWarning: {
    backgroundColor: '#FFFBEB',
    border: '1px solid #FDE68A',
    '&::before': {
      backgroundColor: '#F59E0B'
    },
    '&:hover': {
      backgroundColor: '#FEF3C7',
      transform: 'translateX(2px)',
      boxShadow: '0 2px 8px rgba(245, 158, 11, 0.15)',
      '&::before': {
        width: 4
      }
    }
  },
  alertItemInfo: {
    backgroundColor: '#EFF6FF',
    border: '1px solid #BFDBFE',
    '&::before': {
      backgroundColor: '#3B82F6'
    },
    '&:hover': {
      backgroundColor: '#DBEAFE',
      transform: 'translateX(2px)',
      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.15)',
      '&::before': {
        width: 4
      }
    }
  },
  alertIconWrapper: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  alertIconWrapperError: {
    backgroundColor: '#FEE2E2'
  },
  alertIconWrapperWarning: {
    backgroundColor: '#FEF3C7'
  },
  alertIconWrapperInfo: {
    backgroundColor: '#DBEAFE'
  },
  alertIcon: {
    fontSize: 18
  },
  alertContent: {
    flex: 1,
    minWidth: 0
  },
  alertMainText: {
    fontWeight: 600,
    fontSize: '0.8125rem',
    marginBottom: theme.spacing(0.25),
    display: 'block',
    color: '#1F2937',
    lineHeight: 1.4
  },
  alertSecondaryText: {
    fontSize: '0.75rem',
    color: '#6B7280',
    display: 'block',
    lineHeight: 1.5
  },
  dateBox: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.75),
    padding: '2px 8px',
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    borderRadius: 4,
    fontSize: '0.6875rem',
    color: '#6B7280',
    fontWeight: 500
  },
  dateIcon: {
    fontSize: 11
  },
  errorIcon: {
    color: '#DC2626'
  },
  warningIcon: {
    color: '#D97706'
  },
  infoIcon: {
    color: '#2563EB'
  },
  divider: {
    margin: `${theme.spacing(2)}px 0`
  },
  viewAllButton: {
    textTransform: 'none'
  },
  errorChip: {
    height: 20,
    fontSize: '0.7rem',
    backgroundColor: "red"
  }
}));
export const SidebarAlert = () => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [systemAlerts, setSystemAlerts] = useState([]);
  const [selectedSystemAlert, setSelectedSystemAlert] = useState(null);
  const [systemProgress, setSystemProgress] = useState({});

  // System completion data
  const systemStatus = {
    overall: 65,
    categories: [
      { name: 'Buildings', completed: 8, total: 10, percentage: 80 },
      { name: 'Tenants', completed: 15, total: 20, percentage: 75 },
      { name: 'Contracts', completed: 12, total: 15, percentage: 80 },
      { name: 'Bank Accounts', completed: 2, total: 3, percentage: 67 },
      { name: 'Suppliers', completed: 5, total: 8, percentage: 63 }
    ]
  };

  const totalIssues = systemAlerts.length;

  useEffect(() => {
    getDashboardAlerts().then(response => response.data).then(response => {
      setSystemAlerts(response)
    }).catch(error => {

    })

    getDashboardSystemSetupProgress().then(response => setSystemProgress(response.data)).catch(error => { });
  }, [])

  const handleAlertClick=(alert)=>{
    setSelectedSystemAlert(alert)
  }

  return (
    <Box className={classes.root}>
      {/* Problems & Alerts Section */}
      <Box>
        <Box className={classes.alertsHeader}>
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            {getTranslation("Alerts & Issues","Alerts & Issues","Alerts & Issues")}
          </Typography>
          <IconButton onClick={()=>{
             getDashboardAlerts().then(response => response.data).then(response => {
      setSystemAlerts(response)
    }).catch(error => {

    })
          }}>
            <Refresh/>
          </IconButton>
          <Chip
            label={totalIssues}
            size="small"
            color="secondary"
            className={classes.errorChip}
          />
        </Box>

        <Box style={{
          maxHeight: "380px",       // fixed height
          overflowY: "auto"
        }}>
          {systemAlerts.map((alert, idx) => {
            let IconComponent = WarningIcon;
            if(alert.alertType==0)
              IconComponent=ErrorIcon;
            else if(alert.alertType==1)
              IconComponent=InfoIcon;
            else if(alert.alertType==2)
              IconComponent=WarningIcon;
            return (
              <Box key={alert.id} className={alert.alertType==0? classes.alertItemError : alert.alertType==1?classes.alertItemInfo : classes.alertItemWarning} my={2} p={1}
                onClick={() => handleAlertClick(alert)}
                    style={{ cursor: "pointer" }}   // or put this in your CSS
              >
                {/* <IconComponent 
                  className={`${classes.alertIcon} ${classes[alert.iconClass]}`}
                /> */}
                <Box className={classes.alertContent}>
                  <Typography
                    variant="body2"
                    className={classes.alertMainText}
                  
                  >
                    {alert.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    className={classes.alertSecondaryText}
                  >
                    {alert.message}
                  </Typography>
                  {alert.date && (
                    <Typography
                      variant="caption"
                      className={classes.dateBox}
                      component="div"
                    >
                      <EventIcon className={classes.dateIcon} />
                      {alert.date}
                    </Typography>
                  )}
                </Box>
              </Box>
            );

            // return (
            //   <Accordion
            //     key={idx}
            //     expanded={isExpanded}
            //     onChange={handleAccordionChange(`panel${idx}`)}
            //     className={classes.accordionRoot}
            //   >
            //     <AccordionSummary
            //       expandIcon={<ExpandMoreIcon />}
            //       className={classes.accordionSummary}
            //     >
            //       <Box className={classes.accordionContent}>
            //         <IconComponent 
            //           color={problem.color} 
            //           className={classes.accordionIcon}
            //         />
            //         <Typography 
            //           variant="body2" 
            //           className={classes.accordionTitle}
            //         >
            //           {problem.category}
            //         </Typography>
            //         <Chip 
            //           label={problem.items.length} 
            //           size="small"
            //           color={problem.color}
            //           className={classes.errorChip}
            //         />
            //       </Box>
            //     </AccordionSummary>

            //     <AccordionDetails className={classes.accordionDetails}>
            //       <List dense disablePadding style={{ width: '100%' }}>
            //         {problem.items.map((item, itemIdx) => (
            //           <React.Fragment key={item.id}>
            //             {itemIdx > 0 && <Divider />}
            //             <ListItem className={classes.listItem}>
            //               <Box className={classes.itemContent}>
            //                 <Box className={classes.itemHeader}>
            //                   <Typography 
            //                     variant="body2" 
            //                     className={classes.itemTitle}
            //                   >
            //                     {item.tenant || item.building}
            //                   </Typography>
            //                   {item.priority && (
            //                     <Chip 
            //                       label={item.priority}
            //                       size="small"
            //                       color={getPriorityColor(item.priority)}
            //                       className={classes.priorityChip}
            //                     />
            //                   )}
            //                 </Box>
            //                 <Typography 
            //                   variant="caption" 
            //                   color="textSecondary" 
            //                   display="block"
            //                 >
            //                   {item.issue || item.event}
            //                 </Typography>
            //                 {item.date && (
            //                   <Typography 
            //                     variant="caption" 
            //                     color="textSecondary"
            //                     className={classes.dateBox}
            //                     component="div"
            //                   >
            //                     <EventIcon className={classes.dateIcon} />
            //                     {item.date}
            //                   </Typography>
            //                 )}
            //               </Box>
            //             </ListItem>
            //           </React.Fragment>
            //         ))}
            //       </List>
            //     </AccordionDetails>
            //   </Accordion>
            // );
          })}
        </Box>

      </Box>
      <AlertDetail
        open={selectedSystemAlert}
        onClose={() => setSelectedSystemAlert(null)}
        alert={selectedSystemAlert}
        // onGoTo={(a) => navigate(a.actionUrl || "/indexations")}
        // onResolveStep={(i) => toggleStep(i)}
        // onResolveAll={() => completeAll()}
        // onSnooze={(a, m) => snoozeAlert(a, m)}
      />
    </Box>
  );
};
